import type { MenuOptions } from "@circleco/compass/components/Menu";
import type { ModalPrimaryActionProps } from "@circleco/compass/components/Modal";
import { useCallback, useMemo } from "react";

import { t } from "@/i18n-js/instance";

export type PostSubmitActionStatus = "draft" | "scheduled" | "published";

type SubmitAction = PostSubmitActionStatus | "save";
type SubmissionState = "idle" | "submitting";
type SubmitLabels = Record<SubmissionState, Record<SubmitAction, string>>;

export interface PostSubmitActionOption {
  label: string;
  value: PostSubmitActionStatus;
}

interface UsePostSubmitActionSelectorProps {
  isSubmitting?: boolean;
  onSelectAction: (action: PostSubmitActionOption) => void;
  status?: PostSubmitActionStatus | null;
}

interface GetSubmitLabelProps {
  isEdit?: boolean;
}

interface GetModalPrimaryActionProps extends Omit<
  ModalPrimaryActionProps,
  "label" | "menuOptions"
> {
  isEdit?: boolean;
  shouldShowActionSelector?: boolean;
}

export const usePostSubmitActionSelector = ({
  isSubmitting = false,
  onSelectAction,
  status,
}: UsePostSubmitActionSelectorProps) => {
  const labels = useMemo(() => {
    const labelsByState: SubmitLabels = {
      idle: {
        draft: t("quick_post.scheduled_post_ui.submit_button.save_draft"),
        scheduled: t(
          "quick_post.scheduled_post_ui.submit_button.schedule_post",
        ),
        published: t("quick_post.scheduled_post_ui.submit_button.publish"),
        save: t("quick_post.scheduled_post_ui.submit_button.save"),
      },
      submitting: {
        draft: t("quick_post.scheduled_post_ui.submitting_button.save_draft"),
        scheduled: t(
          "quick_post.scheduled_post_ui.submitting_button.schedule_post",
        ),
        published: t("quick_post.scheduled_post_ui.submitting_button.publish"),
        save: t("quick_post.scheduled_post_ui.submitting_button.save"),
      },
    };

    return labelsByState[isSubmitting ? "submitting" : "idle"];
  }, [isSubmitting]);

  const options = useMemo<PostSubmitActionOption[]>(
    () => [
      { label: labels.draft, value: "draft" },
      { label: labels.scheduled, value: "scheduled" },
      { label: labels.published, value: "published" },
    ],
    [labels],
  );

  const selectedActionValue = status || "published";
  const selectedAction =
    options.find(({ value }) => value === selectedActionValue) ?? options[2];

  const menuOptions: MenuOptions = useMemo(
    () =>
      options
        .filter(({ value }) => value !== selectedAction.value)
        .map(option => ({
          label: option.label,
          onClick: () => onSelectAction(option),
        })),
    [onSelectAction, options, selectedAction],
  );

  const getSubmitLabel = useCallback(
    ({ isEdit = false }: GetSubmitLabelProps = {}) =>
      labels[isEdit ? "save" : "published"],
    [labels],
  );

  const getModalPrimaryAction = useCallback(
    ({
      isEdit = false,
      shouldShowActionSelector = true,
      ...primaryActionProps
    }: GetModalPrimaryActionProps = {}): ModalPrimaryActionProps => ({
      ...primaryActionProps,
      label: shouldShowActionSelector
        ? selectedAction.label
        : getSubmitLabel({ isEdit }),
      menuOptions: shouldShowActionSelector ? menuOptions : undefined,
    }),
    [getSubmitLabel, menuOptions, selectedAction.label],
  );

  return {
    getModalPrimaryAction,
    getSubmitLabel,
    menuOptions,
    options,
    selectedAction,
  };
};
